import { DisposableContext } from "@bodil/core/disposable";
import { getComputedProperty, parseSize } from "@bodil/dom/css";
import { DOMIterator } from "@bodil/dom/dom";
import { eventListener } from "@bodil/dom/event";
import { intersectsHeight } from "@bodil/dom/geometry";
import type { ReactiveController, ReactiveControllerHost } from "lit";

import { KeyboardCommand, keymapLookup } from "../keymap";
import { takeOne } from "../lib";

export type ListNavigationControllerOptions<Item extends Element> = {
    /**
     * The root element, which should be the immediate ancestor of every
     * item element.
     */
    root: () => Element;
    /**
     * The view element, which determines the size of the item viewport. If not
     * specified, we'll use the root element by default.
     */
    view?: () => Element;
    /**
     * A predicate function used to identify item elements. Any child element
     * not matching this will be ignored.
     */
    predicate: (el: unknown) => el is Item;
    /**
     * A callback function used to let the host know a new element has been
     * activated. This function should at a minimum render the new element as
     * activated.
     */
    activate: (el: Item | undefined) => void;
    /**
     * An optional function used to find the active element before processing a
     * navigation event. If this is omitted, this controller is assumed to be
     * the authoritative source of which element is active. If this is provided,
     * this controller should never be considered the authoritative source.
     */
    getActive?: () => Item | undefined;
    /**
     * This function is called before processing a keydown event. If it returns
     * true, we don't process anything. This would be used, for instance, if the
     * viewport element is currently hidden.
     */
    isDisabled?: () => boolean;
    /**
     * If this is true, the controller won't attach a keydown event handler to
     * the host element automatically, relying instead on the user to call
     * {@link ListNavigationController.setKeydownTarget}, if you need it to be
     * something other than the host element.
     */
    willSetKeydownTarget?: boolean;
    /**
     * If this is true, the controller will ignore keypresses that would yield
     * input rather than a selection change. Use this for when you have an input
     * element focused and you don't want to eat actual letters that are mapped
     * to navigation.
     */
    onlyControlKeys?: boolean;
};

export class ListNavigationController<Item extends Element> implements ReactiveController {
    private active?: Item;
    private readonly context = new DisposableContext();

    constructor(
        public host: ReactiveControllerHost & HTMLElement,
        private readonly options: ListNavigationControllerOptions<Item>,
    ) {
        this.host.addController(this);
    }

    setKeydownTarget(target: HTMLElement) {
        this.context.dispose();
        this.context.use(eventListener(target, "keydown", this.handleKeyDown));
    }

    getActive(): Item | undefined {
        return this.options.getActive?.() ?? this.active;
    }

    setActive(el: Item | undefined) {
        this.active = el;
        this.options.activate(el);
    }

    hostConnected(): void {
        if (this.options.willSetKeydownTarget !== true) {
            this.setKeydownTarget(this.host);
        }
    }

    hostDisconnected() {
        this.context.dispose();
    }

    private nextItem(el: Item, extraPredicate?: (el: Item) => boolean): Item | undefined {
        let it: IteratorObject<Item> = new DOMIterator(el).skip().filter(this.options.predicate);
        if (extraPredicate !== undefined) {
            it = it.filter(extraPredicate);
        }
        return takeOne(it).unwrap();
    }

    private previousItem(el: Item, extraPredicate?: (el: Item) => boolean): Item | undefined {
        let it: IteratorObject<Item> = new DOMIterator(el)
            .reverse()
            .skip()
            .filter(this.options.predicate);
        if (extraPredicate !== undefined) {
            it = it.filter(extraPredicate);
        }
        return takeOne(it).unwrap();
    }

    private firstItem(): Item | undefined {
        return takeOne(
            new DOMIterator(this.options.root().firstElementChild).filter(this.options.predicate),
        ).unwrap();
    }

    private lastItem(): Item | undefined {
        return takeOne(
            new DOMIterator(this.options.root().lastElementChild)
                .reverse()
                .filter(this.options.predicate),
        ).unwrap();
    }

    private getViewRect(): DOMRect {
        return (this.options.view?.() ?? this.options.root()).getBoundingClientRect();
    }

    private findMargin(): number {
        return getComputedProperty(this.options.root(), "gap").chain(parseSize).getOr(0);
    }

    private readonly handleKeyDown = (event: KeyboardEvent) => {
        if (this.options.isDisabled?.() === true) {
            return;
        }
        const activeItem = this.getActive();
        const cmd = keymapLookup(event, this.options.onlyControlKeys);
        switch (cmd) {
            case KeyboardCommand.Next:
            case KeyboardCommand.Down: {
                this.setActive(
                    activeItem === undefined || this.nextItem(activeItem) === undefined
                        ? this.firstItem()
                        : this.nextItem(activeItem),
                );
                break;
            }
            case KeyboardCommand.Previous:
            case KeyboardCommand.Up: {
                this.setActive(
                    activeItem === undefined || this.previousItem(activeItem) === undefined
                        ? this.lastItem()
                        : this.previousItem(activeItem),
                );
                break;
            }
            case KeyboardCommand.Bottom: {
                this.setActive(this.lastItem());
                break;
            }
            case KeyboardCommand.Top: {
                this.setActive(this.firstItem());
                break;
            }
            case KeyboardCommand.PageDown: {
                if (activeItem === undefined) {
                    this.setActive(this.firstItem());
                } else {
                    const margin = this.findMargin();
                    const viewHeight = this.getViewRect().height;
                    const activeRect = activeItem.getBoundingClientRect();
                    const targetY = activeRect.top + viewHeight;
                    const target = this.nextItem(activeItem, (el) =>
                        intersectsHeight(el, targetY, margin),
                    );
                    this.setActive(target ?? this.lastItem());
                }
                break;
            }
            case KeyboardCommand.PageUp: {
                if (activeItem === undefined) {
                    this.setActive(this.lastItem());
                } else {
                    const margin = this.findMargin();
                    const viewHeight = this.getViewRect().height;
                    const activeRect = activeItem.getBoundingClientRect();
                    const targetY = activeRect.bottom - viewHeight;
                    const target = this.previousItem(activeItem, (el) =>
                        intersectsHeight(el, targetY, margin),
                    );
                    this.setActive(target ?? this.firstItem());
                }
                break;
            }
            default:
                return;
        }
        event.preventDefault();
        event.stopPropagation();
    };
}
